// Angular Imports
import { Injectable, inject } from '@angular/core'
import { DomSanitizer, SafeUrl } from '@angular/platform-browser'
// Shared Imports
import { FilePreview } from '@shared/models'

@Injectable({
	providedIn: 'root',
})
export class ToolsService {
	readonly #domSanitizer = inject(DomSanitizer)

	convertToBlob({ binaryData, contentType }: ConvertToBlob): Blob {
		return new Blob([binaryData], { type: contentType })
	}

	buildFilePreview(file: File): Promise<FilePreview> {
		return new Promise((resolve) => {
			const reader = new FileReader()
			reader.readAsDataURL(file)
			reader.onload = () => {
				const unsafeImage = window.URL.createObjectURL(file)
				const safeImage = this.#domSanitizer.bypassSecurityTrustUrl(unsafeImage)
				resolve({
					file: file,
					preview: safeImage,
				})
			}
			reader.onerror = (_error) => {
				resolve({
					file: file,
					preview: null,
				})
			}
		})
	}

	renameFile(options: { file: File; newFileName: string; hasExtention?: boolean }) {
		const { file, newFileName, hasExtention = false } = options
		// Obtenemos las partes del nombre del archivo
		const filenameParts = file.name.split('.')
		// Obtenemos la extensión del archivo
		const fileExtension = filenameParts.pop()
		// Agrega la extencion del archivo al nombre en base a la variable hasExtention
		const fileName = hasExtention ? `${newFileName}.${fileExtension}` : `${newFileName}`
		const renamedFile = new File([file], `${fileName}`, { type: file.type })
		// Utilizamos el nuevo nombre y la extensión para crear un nuevo File con el mimetype del original
		return renamedFile
	}

	downloadFile({ filename, binaryData }: DownloadFile) {
		let contentType = ''
		const format = filename.split('.').at(-1)
		switch (format) {
			case 'xlsx':
				contentType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
				break
		}

		const blob = this.convertToBlob({ binaryData, contentType })
		const downloadUrl = URL.createObjectURL(blob)
		const downloadLink = document.createElement('a')
		downloadLink.href = downloadUrl
		downloadLink.download = filename
		downloadLink.click()
		URL.revokeObjectURL(downloadUrl)
	}

	// This function, generateNumbers, takes in an object of type IGenerateNumbers as an argument and returns an array of numbers based on the provided starting point, step value, and maximum value.
	generateNumbers({ start, step, maximun }: IGenerateNumbers): number[] {
		const numbers: number[] = []
		const stepValidated = step ? step : 1
		const startValidated = start ? start : stepValidated

		for (let index = startValidated; index <= maximun; index += stepValidated) {
			numbers.push(index)
		}

		return numbers
	}

	// This function, capitalizeLetters, takes in a string as an argument and returns the same string with the first letter of each word capitalized.
	capitalizeLetters(phrase: string): string {
		const words = phrase.split(' ')
		const wordCapitalized = words.map((word: string) => {
			const firstLetter = word.charAt(0).toUpperCase()
			const othersLetters = word.slice(1)
			return firstLetter + othersLetters
		})
		return wordCapitalized.join(' ')
	}

	// Verify if value is number
	verifyIfValueIsNumber(value: any): boolean {
		return typeof value === 'number' ? true : false
	}

	// Sanitize Url
	sanitizeUrl(url: string): SafeUrl {
		return this.#domSanitizer.bypassSecurityTrustResourceUrl(url)
	}

	/**
	 * Converts a date in ISO format to a string with the format "dd/mm/yyyy".
	 *
	 * @param {string} _dateString - The date in ISO format to convert.
	 * @returns {string | false} - The string in "dd/mm/yyyy" format, or `false` if the date is invalid.
	 */
	getDateFromISODate(_dateString: string): string | false {
		const date = new Date(_dateString)
		const day = date.getUTCDate().toString().padStart(2, '0')
		const month = (date.getUTCMonth() + 1).toString().padStart(2, '0')
		const year = date.getUTCFullYear().toString()

		const dateFormated = `${day}/${month}/${year}`
		return day && month && year ? dateFormated : false
	}

	/**
	 * Retrieves the time in "hh:mm" format from an ISO date.
	 *
	 * @param {string} _dateString - The ISO date from which to retrieve the time.
	 * @returns {string | false} - The time in "hh:mm" format, or `false` if the date is invalid.
	 */
	getTimeFromISODate(_dateString: string): string | false {
		const timeString = _dateString.split('T')[1]
		const timeStringWithoutSeconds = timeString?.slice(0, 5)
		return timeStringWithoutSeconds ? timeStringWithoutSeconds : false
	}
}

interface ConvertToBlob {
	binaryData: ArrayBuffer | string
	contentType: string
}

interface DownloadFile {
	filename: string
	binaryData: ArrayBuffer | string
}

interface IGenerateNumbers {
	start?: number
	step?: number
	maximun: number
}
